import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { Input } from "./ui/input";
import { Search, X, UserRound } from "lucide-react";

const money = (a) => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 0 }).format(a || 0);

export const CustomerPicker = forwardRef(({ customers, value, onChange, placeholder = "Taper le nom ou le téléphone du client…", testId = "select-customer", noneLabel = "Client comptoir (sans compte)", clearTitle = "Changer de client", balanceLabels = { positive: "Doit", negative: "Avance" }, tabIndex }, fwdRef) => {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const ref = useRef(null);
  useImperativeHandle(fwdRef, () => ({ focus: () => ref.current?.focus() }));
  const selected = value && value !== "comptoir" ? customers.find((c) => c.id === value) : null;

  useEffect(() => { setTerm(""); }, [value]);

  const q = term.trim().toLowerCase();
  const matches = q
    ? customers.filter((c) => c.name.toLowerCase().includes(q) || (c.phone || "").replace(/\s/g, "").includes(q.replace(/\s/g, ""))).slice(0, 10)
    : customers.slice(0, 10);

  const pick = (c) => { onChange(c ? c.id : "comptoir"); setOpen(false); setTerm(""); };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setIndex((i) => Math.min(i + 1, matches.length - 1)); setOpen(true); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIndex((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (matches[index]) pick(matches[index]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  if (selected) {
    return (
      <div className="h-9 px-3 flex items-center justify-between gap-2 bg-white border border-green-300 rounded-sm" data-testid={testId}>
        <span className="flex items-center gap-2 min-w-0 text-sm"><UserRound className="w-4 h-4 text-green-700 flex-shrink-0" /><span className="font-medium text-slate-900 whitespace-normal break-words" data-testid="selected-customer-name">{selected.name}</span>
          {selected.balance > 0 && <span className="text-[11px] text-orange-700 whitespace-nowrap">{balanceLabels.positive} {money(selected.balance)}</span>}
          {selected.balance < 0 && <span className="text-[11px] text-teal-700 whitespace-nowrap">{balanceLabels.negative} {money(-selected.balance)}</span>}
        </span>
        <button type="button" onClick={() => { onChange("comptoir"); setTimeout(() => ref.current?.focus(), 30); }} className="text-slate-400 hover:text-red-600" title={clearTitle} data-testid="clear-customer-btn"><X className="w-4 h-4" /></button>
      </div>
    );
  }

  return (
    <div className="relative" data-testid={testId}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <Input
        ref={ref}
        tabIndex={tabIndex}
        value={term}
        onChange={(e) => { setTerm(e.target.value); setIndex(0); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="pl-9 h-9 rounded-sm bg-white"
        autoComplete="off"
        data-testid="customer-search-input"
      />
      {open && (
        <div className="absolute z-40 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-xl max-h-72 overflow-y-auto" data-testid="customer-options">
          {noneLabel && <button type="button" onMouseDown={(e) => { e.preventDefault(); pick(null); }} className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 border-b border-slate-100" data-testid="customer-option-comptoir">{noneLabel}</button>}
          {matches.map((c, i) => (
            <button type="button" key={c.id} onMouseDown={(e) => { e.preventDefault(); pick(c); }} onMouseEnter={() => setIndex(i)} className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm ${i === index ? "bg-green-50" : "hover:bg-slate-50"}`} data-testid={`customer-option-${c.id}`}>
              <span className="min-w-0"><span className="block font-medium text-slate-900 whitespace-normal break-words">{c.name}</span>{c.phone && <span className="block text-[11px] text-slate-400 font-mono">{c.phone}</span>}</span>
              {c.balance > 0 ? <span className="text-[11px] text-orange-700 whitespace-nowrap">{balanceLabels.positive} {money(c.balance)}</span> : c.balance < 0 ? <span className="text-[11px] text-teal-700 whitespace-nowrap">{balanceLabels.negative} {money(-c.balance)}</span> : null}
            </button>
          ))}
          {matches.length === 0 && <p className="px-3 py-3 text-sm text-slate-400">Aucun client ne correspond à « {term} »</p>}
        </div>
      )}
    </div>
  );
});
